/**
 * HistoryManager.ts - HistoryManager - Core of Backtrackr versioning system
 * 
 * CHANGE HISTORY:
 * - 251021_2025 [cc6aae8] danielbeer: fix: Resolve TypeScript migration errors
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */

// Generate numeric IDs for history nodes
let _nodeIdCounter = Date.now();
const generateNodeId = () => _nodeIdCounter++;

/**
 * HistoryManager - Core of Backtrackr versioning system
 * Manages branching history with efficient storage and context-aware undo/redo
 */
class HistoryManager {
  constructor(options: any = {}) {
    this.maxHistorySize = options.maxHistorySize || 1000;
    this.compressionEnabled = options.compressionEnabled !== false;
    
    // Get Actr if provided, otherwise try to get it from Dependr
    this.actr = options.actr || this._tryGetActr();
    
    // Register actions with Actr if available
    if (this.actr) {
      this.registerActions();
    }
    
    // History tree structure
    this.timeline = new Map(); // changeId -> HistoryNode
    this.currentChangeId = null;
    this.rootChangeId = null;
    
    // Context tracking
    this.contexts = new Map(); // contextId -> ContextHistory
    this.activeContext = null;
    
    // Branches (alternate timelines)
    this.branches = new Map(); // branchId -> BranchInfo
    this.currentBranch = 'main';
    
    // Change listeners
    this.listeners = new Set();
    
    this.initialize();
  }

  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      const { getActr } = require('@opcentral/actr');
      return getActr();
    } catch {
      return null;
    }
  }

  /**
   * Initialize the history system
   */
  initialize() {
    const rootChange = this.createHistoryNode({
      type: 'root',
      description: 'Initial state',
      data: null,
      context: null
    });
    
    this.rootChangeId = rootChange.id;
    this.currentChangeId = rootChange.id;
    this.timeline.set(rootChange.id, rootChange);
    
    this.branches.set('main', {
      id: 'main',
      name: 'Main Timeline',
      headChangeId: rootChange.id,
      createdAt: new Date().toISOString()
    });
  }

  /**
   * Register Backtrackr actions with Actr
   * 
   * ARCHITECTURE NOTE: Backtrackr uses ACTIONS-ONLY pattern
   * 
   * Why this is different from notifyr/groupr:
   * 1. In-Memory State Management: Undo/redo is pure in-memory operations
   *    - History tree stored in memory (Map data structures)
   *    - No persistence needed - state is ephemeral
   *    - CRUD would add unnecessary overhead
   * 
   * 2. High-Frequency Operations: Undo/redo called constantly
   *    - Every user action creates history node
   *    - Undo/redo triggered by keyboard shortcuts
   *    - Performance-critical (must be <10ms)
   *    - CRUD overhead would degrade UX
   * 
   * 3. Complex State Transitions: Not simple CRUD operations
   *    - Branching timelines (alternate histories)
   *    - Context-aware undo (field-specific)
   *    - Delta compression for efficiency
   *    - Tree traversal, not linear operations
   * 
   * Pattern: Actions-Only (No CRUD)
   * - Actions: For history operations (record, undo, redo, branch, etc.)
   * - No CRUD: History is in-memory tree structure, not database entities
   * - State-based: Manages application state transitions, not data persistence
   * 
   * Compare to other patterns:
   * - permittr: Actions-Only (performance-critical, in-memory cache)
   * - loggr: Actions-Only (write-heavy, external integrations)
   * - backtrackr: Actions-Only (in-memory state, high-frequency, complex transitions)
   * 
   * See: /docs/ACTR_INTEGRATION_PATTERNS.md for complete explanation
   * See: /packages/backtrackr/README.md "Actr Integration" section
   */
  registerActions() {
    if (!this.actr) return;
    
    // recordChange action (high-frequency)
    this.actr.register('backtrackr:record-change', {
      handler: async (params) => {
        return this.recordChange(params);
      },
      description: 'Record a change in history'
    });
    
    // undo action (keyboard shortcut)
    this.actr.register('backtrackr:undo', {
      handler: async (params) => {
        return this.undo(params?.context);
      },
      description: 'Undo last change'
    });
    
    // redo action (keyboard shortcut)
    this.actr.register('backtrackr:redo', {
      handler: async (params) => {
        return this.redo(params?.context);
      },
      description: 'Redo last undone change'
    });
    
    // createBranch action (alternate timeline)
    this.actr.register('backtrackr:create-branch', {
      handler: async (params) => {
        return this.createBranch(params.name, params.fromChangeId);
      },
      description: 'Create a new branch in history'
    });
    
    // switchBranch action
    this.actr.register('backtrackr:switch-branch', {
      handler: async (params) => {
        return this.switchBranch(params.branchId);
      },
      description: 'Switch to a different branch'
    });
    
    // Note: No CRUD operations - history is in-memory tree structure.
    // State transitions are managed via actions, not database persistence.
  }

  /**
   * Create a history node
   */
  createHistoryNode(data) {
    return {
      id: generateNodeId(),
      timestamp: new Date().toISOString(),
      type: data.type,
      description: data.description,
      context: data.context,
      data: data.data,
      parentId: data.parentId || null,
      children: [],
      branch: data.branch || this.currentBranch,
      metadata: data.metadata || {}
    };
  }

  /**
   * Record a change
   */
  recordChange(change) {
    const {
      type,
      description,
      context,
      before,
      after,
      metadata = {}
    } = change;

    // Create history node
    const node = this.createHistoryNode({
      type,
      description,
      context,
      data: {
        before: this.compressData(before),
        after: this.compressData(after),
        delta: this.calculateDelta(before, after)
      },
      parentId: this.currentChangeId,
      metadata
    });

    // Add to timeline
    this.timeline.set(node.id, node);

    // Update parent's children
    const parent = this.timeline.get(this.currentChangeId);
    if (parent) {
      // Clear any existing children (truncate redo history)
      // This ensures that after undo, a new change replaces the future timeline
      parent.children = [node.id];
    }

    // Update current position
    this.currentChangeId = node.id;

    // Update branch head
    const branch = this.branches.get(this.currentBranch);
    if (branch) {
      branch.headChangeId = node.id;
    }

    // Update context history
    if (context) {
      this.updateContextHistory(context, node.id);
    }

    // Cleanup old history if needed
    this.pruneHistory();

    // Notify listeners
    this.notifyListeners('change', node);

    return node.id;
  }

  /**
   * Calculate delta between two states (for efficient storage)
   */
  calculateDelta(before, after) {
    if (!before || !after) return null;

    const delta = {
      type: 'delta',
      changes: {}
    };

    // For objects, calculate field-level changes
    if (typeof before === 'object' && typeof after === 'object') {
      const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
      
      for (const key of allKeys) {
        if (before[key] !== after[key]) {
          delta.changes[key] = {
            old: before[key],
            new: after[key]
          };
        }
      }
    } else {
      delta.changes = { value: { old: before, new: after } };
    }

    return delta;
  }

  /**
   * Compress data for efficient storage
   */
  compressData(data) {
    if (!this.compressionEnabled) return data;
    
    // Simple compression: store only changed fields
    // In production, could use actual compression algorithms
    return data;
  }

  /**
   * Decompress data
   */
  decompressData(data) {
    return data;
  }

  /**
   * Update context history
   */
  updateContextHistory(contextId, changeId) {
    if (!this.contexts.has(contextId)) {
      this.contexts.set(contextId, {
        id: contextId,
        changes: [],
        currentIndex: -1
      });
    }

    const context = this.contexts.get(contextId);
    
    // If we're not at the end of context history, create a branch
    if (context.currentIndex < context.changes.length - 1) {
      // Truncate future history in this context
      context.changes = context.changes.slice(0, context.currentIndex + 1);
    }

    context.changes.push(changeId);
    context.currentIndex = context.changes.length - 1;
  }

  /**
   * Undo in current context
   */
  async undo(contextId = null) {
    const context = contextId || this.activeContext;

    if (context && this.contexts.has(context)) {
      return this.undoInContext(context);
    } else {
      return this.undoGlobal();
    }
  }

  /**
   * Undo in specific context (field-level)
   */
  async undoInContext(contextId) {
    const context = this.contexts.get(contextId);
    if (!context || context.currentIndex < 0) {
      return { success: false, message: 'Nothing to undo in this context' };
    }

    const changeId = context.changes[context.currentIndex];
    const node = this.timeline.get(changeId);

    if (!node) {
      return { success: false, message: 'Change not found' };
    }

    // Move back in context history
    context.currentIndex--;

    // Get the state to revert to
    const revertState = this.decompressData(node.data.before);

    this.notifyListeners('undo', { contextId, node, revertState });

    return {
      success: true,
      changeId,
      node,
      revertState,
      canUndo: context.currentIndex >= 0,
      canRedo: context.currentIndex < context.changes.length - 1
    };
  }

  /**
   * Redo in specific context
   */
  async redoInContext(contextId) {
    const context = this.contexts.get(contextId);
    if (!context || context.currentIndex >= context.changes.length - 1) {
      return { success: false, message: 'Nothing to redo in this context' };
    }

    // Move forward in context history
    context.currentIndex++;
    const changeId = context.changes[context.currentIndex];
    const node = this.timeline.get(changeId);

    if (!node) {
      return { success: false, message: 'Change not found' };
    }

    const redoState = this.decompressData(node.data.after);

    this.notifyListeners('redo', { contextId, node, redoState });

    return {
      success: true,
      changeId,
      node,
      redoState,
      canUndo: context.currentIndex >= 0,
      canRedo: context.currentIndex < context.changes.length - 1
    };
  }

  /**
   * Global undo (structure-level)
   */
  async undoGlobal() {
    const current = this.timeline.get(this.currentChangeId);
    if (!current || !current.parentId) {
      return { success: false, message: 'Nothing to undo' };
    }

    const parent = this.timeline.get(current.parentId);
    if (!parent) {
      return { success: false, message: 'Parent change not found' };
    }

    this.currentChangeId = parent.id;
    const revertState = this.decompressData(current.data.before);

    this.notifyListeners('undo', { node: current, revertState });

    return {
      success: true,
      changeId: parent.id,
      node: current,
      revertState,
      canUndo: parent.parentId !== null,
      canRedo: true
    };
  }

  /**
   * Global redo
   */
  async redoGlobal() {
    const current = this.timeline.get(this.currentChangeId);
    if (!current || current.children.length === 0) {
      return { success: false, message: 'Nothing to redo' };
    }

    // If multiple children, use the first one (or could prompt user)
    const nextChangeId = current.children[0];
    const next = this.timeline.get(nextChangeId);

    if (!next) {
      return { success: false, message: 'Next change not found' };
    }

    this.currentChangeId = next.id;
    const redoState = this.decompressData(next.data.after);

    this.notifyListeners('redo', { node: next, redoState });

    return {
      success: true,
      changeId: next.id,
      node: next,
      redoState,
      canUndo: true,
      canRedo: next.children.length > 0
    };
  }

  /**
   * Redo (context-aware)
   */
  async redo(contextId = null) {
    const context = contextId || this.activeContext;

    if (context && this.contexts.has(context)) {
      return this.redoInContext(context);
    } else {
      return this.redoGlobal();
    }
  }

  /**
   * Go to specific change in history
   */
  async goToChange(changeId) {
    const node = this.timeline.get(changeId);
    if (!node) {
      return { success: false, message: 'Change not found' };
    }

    // Calculate path from current to target
    const path = this.findPath(this.currentChangeId, changeId);
    
    if (!path) {
      return { success: false, message: 'Cannot find path to change' };
    }

    // Apply changes along the path
    const states = [];
    for (let i = 1; i < path.length; i++) {
      const change = this.timeline.get(path[i]);
      states.push(this.decompressData(change.data.after));
    }

    this.currentChangeId = changeId;
    this.notifyListeners('goto', { changeId, node, states });

    return {
      success: true,
      changeId,
      node,
      states
    };
  }

  /**
   * Find path between two changes
   */
  findPath(fromId, toId) {
    if (fromId === toId) return [fromId];

    // BFS to find path
    const queue = [[fromId]];
    const visited = new Set([fromId]);

    while (queue.length > 0) {
      const path = queue.shift();
      const current = path[path.length - 1];
      const node = this.timeline.get(current);

      if (!node) continue;

      // Check children
      for (const childId of node.children) {
        if (childId === toId) {
          return [...path, childId];
        }
        if (!visited.has(childId)) {
          visited.add(childId);
          queue.push([...path, childId]);
        }
      }

      // Check parent
      if (node.parentId && !visited.has(node.parentId)) {
        if (node.parentId === toId) {
          return [...path, node.parentId];
        }
        visited.add(node.parentId);
        queue.push([...path, node.parentId]);
      }
    }

    return null;
  }

  /**
   * Create a new branch from current position
   */
  createBranch(name, description = '') {
    const branchId = generateNodeId();
    const branch = {
      id: branchId,
      name,
      description,
      parentBranch: this.currentBranch,
      branchPoint: this.currentChangeId,
      headChangeId: this.currentChangeId,
      createdAt: new Date().toISOString()
    };

    this.branches.set(branchId, branch);
    this.currentBranch = branchId;

    this.notifyListeners('branch-created', branch);

    return branch;
  }

  /**
   * Switch to a different branch
   */
  async switchBranch(branchId) {
    const branch = this.branches.get(branchId);
    if (!branch) {
      return { success: false, message: 'Branch not found' };
    }

    const previousBranch = this.currentBranch;
    this.currentBranch = branchId;
    
    // Move to branch head
    const result = await this.goToChange(branch.headChangeId);

    this.notifyListeners('branch-switched', { 
      from: previousBranch, 
      to: branchId,
      branch 
    });

    return result;
  }

  /**
   * Get history for a specific context
   */
  getContextHistory(contextId) {
    const context = this.contexts.get(contextId);
    if (!context) return null;

    return {
      ...context,
      changes: context.changes.map(id => this.timeline.get(id)),
      canUndo: context.currentIndex >= 0,
      canRedo: context.currentIndex < context.changes.length - 1
    };
  }

  /**
   * Get global history tree
   */
  getHistoryTree() {
    const buildTree = (nodeId) => {
      const node = this.timeline.get(nodeId);
      if (!node) return null;

      return {
        ...node,
        children: node.children.map(childId => buildTree(childId)).filter(Boolean)
      };
    };

    return buildTree(this.rootChangeId);
  }

  /**
   * Get linear history from root to current
   */
  getLinearHistory() {
    const history = [];
    let currentId = this.currentChangeId;

    while (currentId) {
      const node = this.timeline.get(currentId);
      if (!node) break;
      
      history.unshift(node);
      currentId = node.parentId;
    }

    return history;
  }

  /**
   * Set active context
   */
  setActiveContext(contextId) {
    this.activeContext = contextId;
    this.notifyListeners('context-changed', { contextId });
  }

  /**
   * Clear active context
   */
  clearActiveContext() {
    this.activeContext = null;
    this.notifyListeners('context-changed', { contextId: null });
  }

  /**
   * Set focused field (convenience method for field-level tracking)
   * Creates a context ID from nodeId and fieldName
   */
  setFocusedField(nodeId, fieldName) {
    const contextId = `${nodeId}.${fieldName}`;
    this.setActiveContext(contextId);
    return contextId;
  }

  /**
   * Clear focused field (convenience method)
   */
  clearFocusedField() {
    this.clearActiveContext();
  }

  /**
   * Record a field change (convenience method for field-level tracking)
   * Automatically creates context ID and records change
   */
  recordFieldChange(nodeId, fieldName, before, after) {
    const contextId = `${nodeId}.${fieldName}`;
    
    return this.recordChange({
      type: 'field-change',
      description: `Changed ${fieldName} in ${nodeId}`,
      context: contextId,
      before,
      after,
      metadata: {
        nodeId,
        fieldName,
        fieldLevel: true
      }
    });
  }

  /**
   * Prune old history to maintain size limit
   */
  pruneHistory() {
    if (this.timeline.size <= this.maxHistorySize) return;

    // Keep recent history and current branch
    // Remove oldest changes that are not on current branch
    const toKeep = new Set();
    const queue = [this.currentChangeId];

    // Mark all nodes on path to current
    while (queue.length > 0) {
      const id = queue.shift();
      if (toKeep.has(id)) continue;
      
      toKeep.add(id);
      const node = this.timeline.get(id);
      
      if (node) {
        if (node.parentId) queue.push(node.parentId);
        node.children.forEach(childId => queue.push(childId));
      }
    }

    // Remove old nodes
    const toRemove = [];
    for (const [id, node] of this.timeline.entries()) {
      if (!toKeep.has(id) && this.timeline.size - toRemove.length > this.maxHistorySize * 0.8) {
        toRemove.push(id);
      }
    }

    toRemove.forEach(id => this.timeline.delete(id));
  }

  /**
   * Add change listener
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('History listener error:', error);
      }
    });
  }

  /**
   * Export history
   */
  export() {
    return {
      timeline: Array.from(this.timeline.entries()),
      contexts: Array.from(this.contexts.entries()),
      branches: Array.from(this.branches.entries()),
      currentChangeId: this.currentChangeId,
      currentBranch: this.currentBranch,
      rootChangeId: this.rootChangeId,
      exportedAt: new Date().toISOString()
    };
  }

  /**
   * Import history
   */
  import(data) {
    this.timeline = new Map(data.timeline);
    this.contexts = new Map(data.contexts);
    this.branches = new Map(data.branches);
    this.currentChangeId = data.currentChangeId;
    this.currentBranch = data.currentBranch;
    this.rootChangeId = data.rootChangeId;

    this.notifyListeners('imported', data);
  }

  /**
   * Get statistics
   */
  getStats() {
    // Calculate undo depth (how many changes back to root)
    let historyDepth = 0;
    let currentId = this.currentChangeId;
    while (currentId !== this.rootChangeId) {
      const change = this.timeline.get(currentId);
      if (!change || !change.parentId) break;
      historyDepth++;
      currentId = change.parentId;
    }
    
    // Calculate redo depth (how many changes forward)
    let futureDepth = 0;
    const currentChange = this.timeline.get(this.currentChangeId);
    if (currentChange && currentChange.children.length > 0) {
      // Count all descendants in the current branch
      const countDescendants = (changeId) => {
        const change = this.timeline.get(changeId);
        if (!change || change.children.length === 0) return 0;
        
        let count = change.children.length;
        change.children.forEach(childId => {
          count += countDescendants(childId);
        });
        return count;
      };
      futureDepth = countDescendants(this.currentChangeId);
    }
    
    return {
      totalChanges: this.timeline.size,
      contexts: this.contexts.size,
      branches: this.branches.size,
      currentBranch: this.currentBranch,
      activeContext: this.activeContext,
      canUndo: this.currentChangeId !== this.rootChangeId,
      canRedo: this.timeline.get(this.currentChangeId)?.children.length > 0,
      historyDepth, // Number of undos available
      futureDepth   // Number of redos available
    };
  }
}

export default HistoryManager;
export { HistoryManager };
